<script setup>
import { onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import IlluminatedButton from '../buttons/IlluminatedButton.vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';


const props = defineProps({
    image: String,
    points: Array,
    title: String,
    content: String,
    buttonLabel: String,
    buttonLink: String,
    reverse: {
        type: Boolean,
        default: false
    }
})

const sectionRef = ref(null);
const imageContainerRef = ref(null);
let tl = null;
let imageAnimation = null;
const splitTexts = [];

onMounted(() => {
    nextTick(async () => {
        const rightSide = sectionRef.value?.querySelector('.right-side');
        const isMobile = window.innerWidth < 767;
        if (!sectionRef.value || !imageContainerRef.value || !rightSide || isMobile) {
            return;
        }

        if ( props.points && props.points.length !== 0){
            // Image parallax animation
            imageAnimation = gsap.to(imageContainerRef.value, {
                yPercent: 50,
                ease: 'none',
                scrollTrigger: {
                    trigger: sectionRef.value,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1.5,
                }
            });
        }

        

        const titleEl = rightSide.querySelector('.title');
        const contentEl = rightSide.querySelector('.content');
        const pointsContainer = rightSide.querySelector('.points-container');
        const points = pointsContainer?.querySelectorAll('.point');
        const buttonEl = pointsContainer?.querySelector('.points-container__button');



        if (contentEl) gsap.set(contentEl, { opacity: 0, filter: 'blur(10px)' });
        if (buttonEl) gsap.set(buttonEl, { opacity: 0, filter: 'blur(10px)' });


        tl = gsap.timeline({
            scrollTrigger: {
                trigger: rightSide,
                start: 'top 80%',
                end: 'center 75%',
                scrub: (points && points.length) ? 1.5 : false,
            },
        });

        
        if (titleEl) {
            tl.fromTo(titleEl, {
                opacity: 0,
                filter: 'blur(10px)',
            },{
                opacity: 1,
                filter: 'blur(0px)',
                stagger: 0.03,
                duration: 0.4,
                ease: 'power2.out',
            });
        }

        if (contentEl) {
            tl.fromTo(contentEl, {
                opacity: 0,
                filter: 'blur(15px)',
            }, {
                opacity: 1,
                filter: 'blur(0px)',
                duration: 0.4,
                ease: 'power2.out',
            }, '<0.2');
        }

        if (points && points.length) {
            await document.fonts.ready;
            for (const point of points) {
                let icon = point.querySelector('.icon');
                let title = point.querySelector('.point__title');
                let content = point.querySelector('.point__content');
                tl.fromTo( icon ,{
                    opacity: 0,
                    filter: 'blur(10px)',
                    scale: 1.5
                },  {
                    opacity: 1,
                    filter: 'blur(0px)',
                    duration: 0.4,
                    ease: 'power2.out',
                }, '+=0.5')

                const splitedTitle = new SplitText(title, { type: 'words,chars' });
                splitTexts.push(splitedTitle);
                tl.fromTo(splitedTitle.chars, {
                    opacity: 0,
                    filter: 'blur(10px)',
                    scale: 1.5
                }, {
                    opacity: 1,
                    filter: 'blur(0px)',
                    scale: 1,
                    stagger: 0.025,
                    duration: 0.35,
                    ease: 'power2.out',
                }, '<0.1');

                if (content) {
                    tl.fromTo(content, {
                        opacity: 0,
                        filter: 'blur(10px)',
                    },{
                        opacity: 1,
                        filter: 'blur(0px)',
                        duration: 1,
                        ease: 'power2.out',
                    }, '-=0.5');
                }
                    
            }
        }
        
        if (buttonEl) {
            tl.fromTo(buttonEl, {
                opacity: 0,
                filter: 'blur(10px)',
                scale: 1.5
            },{
                opacity: 1,
                filter: 'blur(0px)',
                scale: 1,
                duration: 1,
                ease: 'power2.out',
            });
        }

        // Refresh ScrollTrigger after a short delay to ensure correct positioning
        setTimeout(() => {
            ScrollTrigger.refresh();
        }, 100);
    });
});

onBeforeUnmount(() => {
    if (tl) {
        tl.kill();
    }
    if (imageAnimation) {
        imageAnimation.kill();
    }
    for (const s of splitTexts) {
        s.revert();
    }
});
</script>

<template>
    <section class="image-text-section" :class="{ 
        'reverse': reverse ,
        'content-only': !points || points.length === 0
        }" ref="sectionRef">
          <div class="image-container" ref="imageContainerRef">
            <NuxtImg 
                format="webp" 
                :src="image" 
                alt="image"
            />
          </div>

          <div class="right-side">
             <h2 class="title">{{ title }}</h2>
            <div class="content" v-html="content"/>

            <div class="points-container" v-if="points && points.length">
                <div class="point" v-for="(point, index) in points" :key="index">
                    <div class="icon" :style="`background-image: url(${point.icon ? point.icon.url : '/images/stars.svg'});`"></div>
                    <h3 class="point__title">{{ point.title }}</h3>
                    <div class="point__content">
                        <p v-html="point.content"></p>
                    </div>
                </div>

                <IlluminatedButton
                    v-if="buttonLabel && buttonLink"
                    :label="buttonLabel"
                    :link="buttonLink"
                    class="points-container__button"
                />
           </div>
          </div>
           
    </section>

</template>

<style lang="scss" scoped>
.image-text-section {
    
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 25px;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    position: relative;

    &:not(.reverse)::after {
       content: "";
        position: absolute;
        height: 100vh;
        min-height: 1200px;
        width: 100%;
        left:0;
        top: 0;
        border-radius: 1420px;
        opacity: 0.4;
        background: rgba(48, 131, 255, 0.6);
        filter: blur(250px);
        z-index: -1;
    }

    &.reverse {
        flex-direction: row-reverse;
        margin: 150px auto;
    }


    &.content-only {
        gap: 90px;
        display: grid;
        grid-template-columns: 1fr;

        &::after {
            width: 100%;
            aspect-ratio: unset;
            left: 0;
            transform: unset;
        }

        & .image-container {
            width: 100%;
            height: 100%;
            max-height: 80vh;
            flex-grow: 1;
            border-radius: 25px;
            overflow: hidden;
            max-width: unset;

            & img {
                height: 100%;
                max-height: 80vh;
                object-fit: cover;
            }
        }

        & .right-side {
            width: 100%;
            max-width: 600px;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
    }

   & .image-container {
        flex: 1;
        max-width: 750px;
        width: 100%;

         & img {
            width: 100%;
            height: auto;
            object-fit: contain;
        }
    }

    & .right-side {
        width: 100%;
        margin: 0 auto;
        max-width: 450px;

        & .title {
            font-size: $title-size;
            margin-bottom: 45px;
        }

        & .content {
            color: $white;
            font-size: 17px;
            letter-spacing: -.7px;
        }
    }
    & .points-container {
        display: flex;
        flex-direction: column;
        gap: 90px;
        width: 100%;
        max-width: 600px;

        & .point {
            display: flex;
            flex-direction: column;
            gap: 10px;
            max-width: 400px;

            & .icon {
                width: 33px;
                aspect-ratio: 1;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
            }

            &__title {
                font-size: $upper-title-size;
                color: $white;
            }

            &__content {
               
            }
        }
    }

}


// Responsive styles
@media screen and (min-width: 767px) {
    .image-text-section {
        flex-direction: row;
        gap: 225px;

        &:not(.reverse)::after {
            width: 75vw;
            height: unset;
            min-height: unset;
            right: unset;
            left: 50%;
            transform: translate(-45%, 15%);
            aspect-ratio: 14 / 11;
            left: -20vw;
        }

         &.content-only {
            grid-template-columns: 1fr 1fr;

            &::after {
                width: unset;
                aspect-ratio: unset;
                left: 0;
                transform: unset;
            }
             &.reverse {
                .image-container {
                    order: 2;
                }
            }
         }

        & .right-side {
            width: 40%;
            margin: unset;
        }
    }

    .image-text-section .image-container{
        top: -150px;
        position: relative;
    }
}
</style>
